import { Point } from "geojson";
import { SmartTargetComparison } from "../components/CustomImprovements/SmartTargets/SmartTargetFilterItem";
import Role from "../rest/Role";
import { BaseProperty, Property } from "../rest/responses/Property";

export type Scalar = string | number | boolean | null;
export type Values = string[];
// Date columns use date strings rather than numbers
export type RangeBound = number | string;
export type RangeWithoutMinimum = [null, RangeBound];
export type RangeWithoutMaximum = [RangeBound, null];
export type Range = [RangeBound, RangeBound] | RangeWithoutMaximum | RangeWithoutMinimum;
export type KeyValuePair = { [key: string]: string | number | boolean | null };

// This file is becoming a bit unwieldy. I am beginning to split this out into multiple files with new features.
export interface TimestampedRecord {
	id: number;
	created_at: string;
	updated_at: string;
}

export interface Group {
	id: number;
	name: string;
	description?: string;
	date: string;
	is_favourite?: boolean;
	is_hidden: boolean;
	inserted_at: string;
}

export interface CustomImprovement {
	id: number;
	name: string;
	description: string;
	accepts_parameters: boolean;
	min?: number;
	max?: number;
	units?: string;
	default?: number;
}

export interface SapImprovementTypes {
	energy_saving: number;
	co2_saving: number;
	cost_saving: number;
	sap_value: number;
	sap_rating: string;
	co2_value: number;
	co2_rating: string;
	heat_demand: number;
	hlp: number;
}

export interface SapImprovementResults extends TimestampedRecord, SapImprovementTypes {
	custom_improvement_sets_id: number;
}

export interface CustomImprovementsSet {
	id: number;
	name: string;
	result: SapImprovementResults;
}

export interface CustomImprovementSetWithImprovement extends CustomImprovementsSet {
	custom_improvements: CustomImprovementSetHasCustomImprovement[];
}

export interface CustomImprovementSetHasCustomImprovement {
	custom_improvement_sets_id: number;
	custom_improvements_id: number;
	improvement: CustomImprovement;
	parameters?: string;
	order?: number;
	reason?: string;
	result?: SapImprovementResults;
}

export interface Batch {
	id: string;
	progress: number;
	pending_jobs: number;
	failed_jobs: number;
	total_jobs: number;
	created_at: number;
	cancelled_at: number;
	finished_at: number;
}

export interface Export extends TimestampedRecord {
	status: JobStatus;
	progress: number;
	filename: string | null;
	download_url: string | null;
}

export interface BulkCustomImprovementSetsBatch extends Batch {
	options: {
		team_id: number;
	};
}

export interface BulkCustomImprovementsAggregate {
	progress: number;
	total_jobs: number;
	total_pending: number;
	total_failed: number;
	cancelled_at: number | null;
	finished_at: number | null;
}

export interface PropertyFeature {
	type: string;
	geometry: Point;
	properties: {
		data: Property;
		"metrics.epc_rating": number;
		"image_meta.insulation_performance": number;
	};
}

export interface User {
	id: number;
	team_id: number;
	is_admin: boolean;
	email: string;
	role: Role;
	extra_permissions?: string[];
	picture: string;
	nickname: string;
}

/** Columns that are able to be matched with a LIKE clause */
interface LikeMatchColumns {
	uprn?: string | string[];
	addr_no?: string | string[];
	addr_street?: string | string[];
	addr_city?: string | string[];
	postcode?: string | string[];
	no_of_storeys?: string | string[];
	region?: number | number[];
	lat?: number | number[];
	lng?: number | number[];
	age?: string | string[];
	detachment?: string | string[];
	property_type?: string | string[];
	wall_type?: string | string[];
	property_description?: string | string[];
	block_ref?: string | string[];
	scheme_ref?: string | string[];
	notes_1?: string | string[];
	notes_2?: string | string[];
}

/** Columns that are able to be matched with an = */
interface EqualsMatchColumns {
	construction_age?: number | number[];
	roof_type?: number | number[];
	fuel_type?: string | string[];
}

/** Numeric columns that are able to be matched between two values */
interface BetweenMatchColumns {
	epc_rating?: [number, number];
	data_integrity?: [number, number];
	current_heat_demand?: [number, number];
	potential_heat_demand?: [number, number];
	wall_construction?: [number, number];
	glazing_u_value?: [number, number];
	roof_construction?: [number, number];
	main_fuel_price?: [number, number];
	main_fuel_co2?: [number, number];
	secondary_fuel_price?: [number, number];
	secondary_fuel_co2?: [number, number];
	odc_lodgement_date?: [number, number];
}

/** Filters for querying for properties */
export type PropertyFilters = {
	address?: string;
	epc_rating_grade?: string | string[];
	group_ids?: number[];
	polygons?: string[];
	has_images?: boolean | string;
} & LikeMatchColumns &
	EqualsMatchColumns &
	BetweenMatchColumns;

export enum SmartTargetFields {
	ENERGY_SAVING = "energy_saving",
	CO2_SAVING = "co2_saving",
	COST_SAVING = "cost_saving",
	SAP_VALUE = "sap_value",
	CO2_VALUE = "co2_value",
	HEAT_DEMAND = "heat_demand",
	HLP = "hlp",
}

export interface SmartTargetParams {
	target: SmartTargetFields;
	value?: number;
	weights: { [weight in SmartTargetWeight]: boolean };
	filters: SmartTargetFilter[];
	logging?: boolean;
}

export enum JobStatus {
	RUNNING = "running",
	FINISHED = "finished",
	CANCELLED = "cancelled",
	ERROR = "error",
	PENDING = "pending",
}

export enum SmartTargetStatus {
	RUNNING = "running",
	FINISHED = "finished",
	CANCELLED = "cancelled",
	ERROR = "error",
	PENDING = "pending",
	UNACHIEVED = "unachieved",
}

export interface SmartTarget extends TimestampedRecord {
	target: SmartTargetFields;
	value: number;
	weights?: SmartTargetWeight[];
	filters?: SmartTargetFilter[];
}

export interface SmartTargetHasProperty {
	status: SmartTargetStatus;
	customImprovementSet?: CustomImprovementsSet | CustomImprovementSetWithImprovement;
	logs?: SmartTargetLogEntry[];
}

export type SmartTargetWithPivot = SmartTarget & SmartTargetHasProperty;

export interface SmartTargetLogEntry {
	id: number;
	message: string;
	log_level: number;
	log_time: string;
}

export interface SmartTargetFilter {
	customImprovement: CustomImprovement;
	hidden: boolean;
	included: boolean;
	operator?: SmartTargetComparison;
	value?: number;
	if_operator?: SmartTargetComparison;
	if_value?: number;
}

export type SmartTargetFieldMap = Record<SmartTargetFields, { current?: number; validateAbove: boolean }>;

export type SmartTargetWeight = "cost" | "disruption";

export type SapImprovementUnit = "£" | "kWh" | "Kg" | null;

type UnitTimeFrame = { unit: SapImprovementUnit; timeFrame: "year" | "month" | "week" | "day" | null };
// not exactly a type, feel free to move to somewhere else...
export const sapImprovementUnitsMap: Record<keyof SapImprovementTypes, UnitTimeFrame> = {
	co2_rating: { unit: null, timeFrame: null },
	co2_saving: { unit: "Kg", timeFrame: "year" },
	co2_value: { unit: null, timeFrame: null },
	cost_saving: { unit: "£", timeFrame: "year" },
	energy_saving: { unit: "kWh", timeFrame: "year" },
	heat_demand: { unit: null, timeFrame: null },
	hlp: { unit: null, timeFrame: null },
	sap_rating: { unit: null, timeFrame: null },
	sap_value: { unit: null, timeFrame: null },
};

export const SapImprovementNiceNames: Record<keyof SapImprovementTypes, string> = {
	co2_rating: "CO2 Rating",
	co2_saving: "CO2 Saving",
	co2_value: "CO2 Value",
	cost_saving: "Cost Saving",
	energy_saving: "Energy Saving",
	heat_demand: "Heat Demand",
	hlp: "HLP",
	sap_rating: "SAP Rating",
	sap_value: "SAP Value",
};

export type Connotation = "positive" | "negative" | "neutral";

export enum SapVersion {
	VERSION_2012 = "2012",
	VERSION_10_02 = "10.02",
}

export interface Filter {
	accessor: Accessor;
	term: Scalar | Range;
}

export interface PropertyFilter {
	attribute: PropertyAttribute;
	term: Scalar | Range;
}

export type PropertyQueryFilter = "address" | "group_ids" | "polygons" | "has_images" | "quick_search";
export interface QueryFilter {
	filter: PropertyQueryFilter;
	term: Scalar | Scalar[] | number[] | string[];
}

export type V3PropertyFilter = Filter | PropertyFilter | QueryFilter;

export interface ColumnSelectionPreset extends TimestampedRecord {
	name: string;
	menu_order: number;
	selected_columns: string[];
}

export type ImageUrl = { [key in number | "original"]: string };

/** These are the values stored against each accessor in data_integrity.integrity json. */
export enum DataIntegritySource {
	ASSUMED = 1,
	CONFIRMED = 2,
	CLONED = 3,
	EPC = 4,
	SUPPLIED = 5,
	API = 6,
}

export type Constituent =
	| "airflow"
	| "building"
	| "data_integrity"
	| "dimensions"
	| "electricity"
	| "heating"
	| "image_meta"
	| "location"
	| "metrics"
	| "misc"
	| "roof"
	| "sap_data"
	| "secondary_heating"
	| "solar"
	| "water"
	| "windows";
export type AirflowAttributes =
	| "closed_fire_flues"
	| "flues"
	| "mech_vent_air_exchange_rate"
	| "misc_heating_blocked_chimneys"
	| "misc_heating_flueless_gas_fires"
	| "misc_heating_intermittent_fans"
	| "misc_heating_passive_vents"
	| "other_flues"
	| "other_heating_chimneys"
	| "other_heating_closed_fire_chimneys_flues"
	| "other_heating_flues"
	| "other_heating_open_flues"
	| "other_heating_solid_boiler_flues"
	| "secondary_heating_chimneys"
	| "secondary_heating_closed_fire_chimney_flues"
	| "secondary_heating_flues"
	| "secondary_heating_other_heater_flues"
	| "secondary_heating_solid_boiler_flues"
	| "solid_boiler_flues"
	| "ventilation_type";
export type AirflowAccessors =
	| "airflow.closed_fire_flues"
	| "airflow.flues"
	| "airflow.mech_vent_air_exchange_rate"
	| "airflow.misc_heating_blocked_chimneys"
	| "airflow.misc_heating_flueless_gas_fires"
	| "airflow.misc_heating_intermittent_fans"
	| "airflow.misc_heating_passive_vents"
	| "airflow.other_flues"
	| "airflow.other_heating_chimneys"
	| "airflow.other_heating_closed_fire_chimneys_flues"
	| "airflow.other_heating_flues"
	| "airflow.other_heating_open_flues"
	| "airflow.other_heating_solid_boiler_flues"
	| "airflow.secondary_heating_chimneys"
	| "airflow.secondary_heating_closed_fire_chimney_flues"
	| "airflow.secondary_heating_flues"
	| "airflow.secondary_heating_other_heater_flues"
	| "airflow.secondary_heating_solid_boiler_flues"
	| "airflow.solid_boiler_flues"
	| "airflow.ventilation_type";
export type BuildingAttributes =
	| "age"
	| "construction_age"
	| "detachment"
	| "external_wall_1_anomaly_area"
	| "external_wall_1_anomaly_u_value"
	| "external_wall_2_anomaly_area"
	| "external_wall_2_anomaly_u_value"
	| "external_wall_3_anomaly_area"
	| "external_wall_3_anomaly_u_value"
	| "external_wall_4_anomaly_area"
	| "external_wall_4_anomaly_u_value"
	| "external_wall_type"
	| "floor_construction"
	| "floor_u_value"
	| "floorconstruction"
	| "has_draught_lobby"
	| "has_suspended_wooden_ground_floor"
	| "party_wall_construction"
	| "party_wall_type_2"
	| "property_type"
	| "structural_infiltration"
	| "wall_construction"
	| "wall_construction_2"
	| "wall_type";
export type BuildingAccessors =
	| "building.age"
	| "building.construction_age"
	| "building.detachment"
	| "building.external_wall_1_anomaly_area"
	| "building.external_wall_1_anomaly_u_value"
	| "building.external_wall_2_anomaly_area"
	| "building.external_wall_2_anomaly_u_value"
	| "building.external_wall_3_anomaly_area"
	| "building.external_wall_3_anomaly_u_value"
	| "building.external_wall_4_anomaly_area"
	| "building.external_wall_4_anomaly_u_value"
	| "building.external_wall_type"
	| "building.floor_construction"
	| "building.floor_u_value"
	| "building.floorconstruction"
	| "building.has_draught_lobby"
	| "building.has_suspended_wooden_ground_floor"
	| "building.party_wall_construction"
	| "building.party_wall_type_2"
	| "building.property_type"
	| "building.structural_infiltration"
	| "building.wall_construction"
	| "building.wall_construction_2"
	| "building.wall_type";
export type DataIntegrityAttributes = "integrity" | "value";
export type DataIntegrityAccessors = "data_integrity.integrity" | "data_integrity.value";
export type DimensionsAttributes =
	| "basement_floor_1_net_area"
	| "basement_floor_2_net_area"
	| "basement_floor_3_net_area"
	| "basement_floor_4_net_area"
	| "basement_floor_area"
	| "basement_room_height"
	| "basement_wall_1_gross_area"
	| "basement_wall_1_openings_area"
	| "basement_wall_2_gross_area"
	| "basement_wall_2_openings_area"
	| "basement_wall_3_gross_area"
	| "basement_wall_3_openings_area"
	| "basement_wall_4_gross_area"
	| "basement_wall_4_openings_area"
	| "bedrooms"
	| "door_environment"
	| "door_environment_2"
	| "ex1_area"
	| "ex1_room_height"
	| "exposed_floor_1_net_area"
	| "exposed_floor_2_net_area"
	| "exposed_floor_3_net_area"
	| "exposed_floor_4_net_area"
	| "exposed_perimeter"
	| "ext_wall_area"
	| "external_glazed_door_count"
	| "external_solid_door_count"
	| "external_wall_1_gross_area"
	| "external_wall_1_openings_area"
	| "external_wall_2_gross_area"
	| "external_wall_2_openings_area"
	| "external_wall_3_gross_area"
	| "external_wall_3_openings_area"
	| "external_wall_4_gross_area"
	| "external_wall_4_openings_area"
	| "floor_area1"
	| "floor_area2"
	| "floor_area3"
	| "floor_area_4"
	| "floor_insulation_depth"
	| "ground_floor_1_net_area"
	| "ground_floor_2_net_area"
	| "ground_floor_3_net_area"
	| "ground_floor_4_net_area"
	| "internal_ceiling_1_net_area"
	| "internal_ceiling_2_net_area"
	| "internal_ceiling_3_net_area"
	| "internal_ceiling_4_net_area"
	| "internal_floor_1_net_area"
	| "internal_floor_2_net_area"
	| "internal_floor_3_net_area"
	| "internal_floor_4_net_area"
	| "internal_wall_1_net_area"
	| "internal_wall_2_net_area"
	| "internal_wall_3_net_area"
	| "internal_wall_4_net_area"
	| "living_area"
	| "no_of_storeys"
	| "party_ceiling_1_net_area"
	| "party_ceiling_2_net_area"
	| "party_ceiling_3_net_area"
	| "party_ceiling_4_net_area"
	| "party_floor_1_net_area"
	| "party_floor_2_net_area"
	| "party_floor_3_net_area"
	| "party_floor_4_net_area"
	| "party_wall_1_net_area"
	| "party_wall_2_net_area"
	| "party_wall_3_net_area"
	| "party_wall_4_net_area"
	| "party_wall_area"
	| "party_wall_u_value"
	| "percent_living"
	| "property_shape"
	| "roof_1_gross_area"
	| "roof_1_openings_area"
	| "roof_2_gross_area"
	| "roof_2_openings_area"
	| "roof_3_gross_area"
	| "roof_3_openings_area"
	| "roof_4_gross_area"
	| "roof_4_openings_area"
	| "room_height1"
	| "room_height2"
	| "room_height3"
	| "room_height_4"
	| "semi_glazed_door"
	| "semi_glazed_door_net_area"
	| "sheltered_sides_count"
	| "sheltered_wall_area"
	| "sheltered_wall_u_value"
	| "solid_door_net_area"
	| "solid_door_type"
	| "unheated_corridor_length"
	| "wall_2_percent"
	| "wall_insulation_thickness"
	| "wall_thickness";
export type DimensionsAccessors =
	| "dimensions.basement_floor_1_net_area"
	| "dimensions.basement_floor_2_net_area"
	| "dimensions.basement_floor_3_net_area"
	| "dimensions.basement_floor_4_net_area"
	| "dimensions.basement_floor_area"
	| "dimensions.basement_room_height"
	| "dimensions.basement_wall_1_gross_area"
	| "dimensions.basement_wall_1_openings_area"
	| "dimensions.basement_wall_2_gross_area"
	| "dimensions.basement_wall_2_openings_area"
	| "dimensions.basement_wall_3_gross_area"
	| "dimensions.basement_wall_3_openings_area"
	| "dimensions.basement_wall_4_gross_area"
	| "dimensions.basement_wall_4_openings_area"
	| "dimensions.bedrooms"
	| "dimensions.door_environment"
	| "dimensions.door_environment_2"
	| "dimensions.ex1_area"
	| "dimensions.ex1_room_height"
	| "dimensions.exposed_floor_1_net_area"
	| "dimensions.exposed_floor_2_net_area"
	| "dimensions.exposed_floor_3_net_area"
	| "dimensions.exposed_floor_4_net_area"
	| "dimensions.exposed_perimeter"
	| "dimensions.ext_wall_area"
	| "dimensions.external_glazed_door_count"
	| "dimensions.external_solid_door_count"
	| "dimensions.external_wall_1_gross_area"
	| "dimensions.external_wall_1_openings_area"
	| "dimensions.external_wall_2_gross_area"
	| "dimensions.external_wall_2_openings_area"
	| "dimensions.external_wall_3_gross_area"
	| "dimensions.external_wall_3_openings_area"
	| "dimensions.external_wall_4_gross_area"
	| "dimensions.external_wall_4_openings_area"
	| "dimensions.floor_area1"
	| "dimensions.floor_area2"
	| "dimensions.floor_area3"
	| "dimensions.floor_area_4"
	| "dimensions.floor_insulation_depth"
	| "dimensions.ground_floor_1_net_area"
	| "dimensions.ground_floor_2_net_area"
	| "dimensions.ground_floor_3_net_area"
	| "dimensions.ground_floor_4_net_area"
	| "dimensions.internal_ceiling_1_net_area"
	| "dimensions.internal_ceiling_2_net_area"
	| "dimensions.internal_ceiling_3_net_area"
	| "dimensions.internal_ceiling_4_net_area"
	| "dimensions.internal_floor_1_net_area"
	| "dimensions.internal_floor_2_net_area"
	| "dimensions.internal_floor_3_net_area"
	| "dimensions.internal_floor_4_net_area"
	| "dimensions.internal_wall_1_net_area"
	| "dimensions.internal_wall_2_net_area"
	| "dimensions.internal_wall_3_net_area"
	| "dimensions.internal_wall_4_net_area"
	| "dimensions.living_area"
	| "dimensions.no_of_storeys"
	| "dimensions.party_ceiling_1_net_area"
	| "dimensions.party_ceiling_2_net_area"
	| "dimensions.party_ceiling_3_net_area"
	| "dimensions.party_ceiling_4_net_area"
	| "dimensions.party_floor_1_net_area"
	| "dimensions.party_floor_2_net_area"
	| "dimensions.party_floor_3_net_area"
	| "dimensions.party_floor_4_net_area"
	| "dimensions.party_wall_1_net_area"
	| "dimensions.party_wall_2_net_area"
	| "dimensions.party_wall_3_net_area"
	| "dimensions.party_wall_4_net_area"
	| "dimensions.party_wall_area"
	| "dimensions.party_wall_u_value"
	| "dimensions.percent_living"
	| "dimensions.property_shape"
	| "dimensions.roof_1_gross_area"
	| "dimensions.roof_1_openings_area"
	| "dimensions.roof_2_gross_area"
	| "dimensions.roof_2_openings_area"
	| "dimensions.roof_3_gross_area"
	| "dimensions.roof_3_openings_area"
	| "dimensions.roof_4_gross_area"
	| "dimensions.roof_4_openings_area"
	| "dimensions.room_height1"
	| "dimensions.room_height2"
	| "dimensions.room_height3"
	| "dimensions.room_height_4"
	| "dimensions.semi_glazed_door"
	| "dimensions.semi_glazed_door_net_area"
	| "dimensions.sheltered_sides_count"
	| "dimensions.sheltered_wall_area"
	| "dimensions.sheltered_wall_u_value"
	| "dimensions.solid_door_net_area"
	| "dimensions.solid_door_type"
	| "dimensions.unheated_corridor_length"
	| "dimensions.wall_2_percent"
	| "dimensions.wall_insulation_thickness"
	| "dimensions.wall_thickness";
export type ElectricityAttributes =
	| "lighting"
	| "lighting_fuel_price"
	| "electricity_tariff"
	| "lighting_led_count"
	| "lighting_incandescent_count"
	| "lighting_cf2_count"
	| "electricity_high_rate_fuel_cost"
	| "electricity_low_rate_fuel_cost"
	| "electricity_high_rate_fraction"
	| "electricity_co2"
	| "wh_electricity_fuel_tariff";
export type ElectricityAccessors =
	| "electricity.lighting"
	| "electricity.lighting_fuel_price"
	| "electricity.electricity_tariff"
	| "electricity.lighting_led_count"
	| "electricity.lighting_incandescent_count"
	| "electricity.lighting_cf2_count"
	| "electricity.electricity_high_rate_fuel_cost"
	| "electricity.electricity_low_rate_fuel_cost"
	| "electricity.electricity_high_rate_fraction"
	| "electricity.electricity_co2"
	| "electricity.wh_electricity_fuel_tariff";
export type HeatingAttributes =
	| "boiler_type"
	| "calculate_primary_circuit_loss"
	| "combi_boiler_profile"
	| "combi_boiler_sub_type"
	| "combi_boiler_type"
	| "control"
	| "controls"
	| "fghrs_pcnt"
	| "fuel_type"
	| "has_boiler_interlock"
	| "has_combined_cylinder"
	| "has_heat_network"
	| "heating_efficiency"
	| "heating_efficiency_rating"
	| "heating_high_rate_fraction"
	| "heating_low_rate_fraction"
	| "heating_type"
	| "high_rate_fuel_cost"
	| "ig_pump_type"
	| "loss_factor_f1"
	| "loss_factor_f2"
	| "loss_factor_f3"
	| "low_rate_fuel_cost"
	| "main_fuel_co2"
	| "main_fuel_price"
	| "main_heating_1_fuel"
	| "main_heating_1_high_rate_fraction"
	| "main_heating_1_high_rate_fuel_cost"
	| "main_heating_1_low_rate_fuel_cost"
	| "main_heating_1_summer_efficiency"
	| "main_heating_2_co2"
	| "main_heating_2_efficiency"
	| "main_heating_2_fraction"
	| "main_heating_2_fuel"
	| "main_heating_2_high_rate_fraction"
	| "main_heating_2_high_rate_fuel_cost"
	| "main_heating_2_low_rate_fuel_cost"
	| "main_heating_2_type"
	| "main_heating_code"
	| "main_heating_control_type"
	| "main_heating_emitter"
	| "pipe_work_insulation_fraction"
	| "primary_space_heating_1_fuel"
	| "primary_space_heating_2_fuel"
	| "rejected_energy_proportion"
	| "standing_charge"
	| "tariff"
	| "temp_adjustment"
	| "temperature_factor"
	| "wh_fghrs_name";
export type HeatingAccessors =
	| "heating.boiler_type"
	| "heating.calculate_primary_circuit_loss"
	| "heating.combi_boiler_profile"
	| "heating.combi_boiler_sub_type"
	| "heating.combi_boiler_type"
	| "heating.control"
	| "heating.controls"
	| "heating.fghrs_pcnt"
	| "heating.fuel_type"
	| "heating.has_boiler_interlock"
	| "heating.has_combined_cylinder"
	| "heating.has_heat_network"
	| "heating.heating_efficiency"
	| "heating.heating_efficiency_rating"
	| "heating.heating_high_rate_fraction"
	| "heating.heating_low_rate_fraction"
	| "heating.heating_type"
	| "heating.high_rate_fuel_cost"
	| "heating.ig_pump_type"
	| "heating.loss_factor_f1"
	| "heating.loss_factor_f2"
	| "heating.loss_factor_f3"
	| "heating.low_rate_fuel_cost"
	| "heating.main_fuel_co2"
	| "heating.main_fuel_price"
	| "heating.main_heating_1_fuel"
	| "heating.main_heating_1_high_rate_fraction"
	| "heating.main_heating_1_high_rate_fuel_cost"
	| "heating.main_heating_1_low_rate_fuel_cost"
	| "heating.main_heating_1_summer_efficiency"
	| "heating.main_heating_2_co2"
	| "heating.main_heating_2_efficiency"
	| "heating.main_heating_2_fraction"
	| "heating.main_heating_2_fuel"
	| "heating.main_heating_2_high_rate_fraction"
	| "heating.main_heating_2_high_rate_fuel_cost"
	| "heating.main_heating_2_low_rate_fuel_cost"
	| "heating.main_heating_2_type"
	| "heating.main_heating_code"
	| "heating.main_heating_control_type"
	| "heating.main_heating_emitter"
	| "heating.pipe_work_insulation_fraction"
	| "heating.primary_space_heating_1_fuel"
	| "heating.primary_space_heating_2_fuel"
	| "heating.rejected_energy_proportion"
	| "heating.standing_charge"
	| "heating.tariff"
	| "heating.temp_adjustment"
	| "heating.temperature_factor"
	| "heating.wh_fghrs_name";
export type ImageMetaAttributes =
	| "analysis"
	| "additional_comments"
	| "result"
	| "affected_areas"
	| "insulation_performance"
	| "area_of_concern"
	| "image_urls";
export type ImageMetaAccessors =
	| "image_meta.analysis"
	| "image_meta.additional_comments"
	| "image_meta.result"
	| "image_meta.affected_areas"
	| "image_meta.insulation_performance"
	| "image_meta.area_of_concern"
	| "image_meta.image_urls";
export type LocationAttributes =
	| "addr_city"
	| "addr_no"
	| "addr_street"
	| "country"
	| "geom"
	| "lat"
	| "lng"
	| "lsoa"
	| "msoa"
	| "postcode"
	| "region";
export type LocationAccessors =
	| "location.addr_city"
	| "location.addr_no"
	| "location.addr_street"
	| "location.country"
	| "location.geom"
	| "location.lat"
	| "location.lng"
	| "location.lsoa"
	| "location.msoa"
	| "location.postcode"
	| "location.region";
export type MetricsAttributes =
	| "ap4"
	| "ap50"
	| "balanced_heat_recovery_efficiency"
	| "basement_floor_1_k_value"
	| "basement_floor_1_u_value"
	| "basement_floor_2_k_value"
	| "basement_floor_2_u_value"
	| "basement_floor_3_k_value"
	| "basement_floor_3_u_value"
	| "basement_floor_4_k_value"
	| "basement_floor_4_u_value"
	| "basement_wall_1_k_value"
	| "basement_wall_1_u_value"
	| "basement_wall_2_k_value"
	| "basement_wall_2_u_value"
	| "basement_wall_3_k_value"
	| "basement_wall_3_u_value"
	| "basement_wall_4_k_value"
	| "basement_wall_4_u_value"
	| "custom_measures"
	| "dynamic_measures"
	| "epc_rating"
	| "exposed_floor_1_k_value"
	| "exposed_floor_1_u_value"
	| "exposed_floor_2_k_value"
	| "exposed_floor_2_u_value"
	| "exposed_floor_3_k_value"
	| "exposed_floor_3_u_value"
	| "exposed_floor_4_k_value"
	| "exposed_floor_4_u_value"
	| "external_wall_1_k_value"
	| "external_wall_1_u_value"
	| "external_wall_2_k_value"
	| "external_wall_2_u_value"
	| "external_wall_3_k_value"
	| "external_wall_3_u_value"
	| "external_wall_4_k_value"
	| "external_wall_4_u_value"
	| "ground_floor_1_k_value"
	| "ground_floor_1_u_value"
	| "ground_floor_2_k_value"
	| "ground_floor_2_u_value"
	| "ground_floor_3_k_value"
	| "ground_floor_3_u_value"
	| "ground_floor_4_k_value"
	| "ground_floor_4_u_value"
	| "internal_ceiling_1_k_value"
	| "internal_ceiling_2_k_value"
	| "internal_ceiling_3_k_value"
	| "internal_ceiling_4_k_value"
	| "internal_floor_1_k_value"
	| "internal_floor_2_k_value"
	| "internal_floor_3_k_value"
	| "internal_floor_4_k_value"
	| "internal_wall_1_k_value"
	| "internal_wall_2_k_value"
	| "internal_wall_3_k_value"
	| "internal_wall_4_k_value"
	| "lodged_sap_score"
	| "odc_last_accepted_suggestion"
	| "odc_lodgement_date"
	| "party_ceiling_1_k_value"
	| "party_ceiling_2_k_value"
	| "party_ceiling_3_k_value"
	| "party_ceiling_4_k_value"
	| "party_floor_1_k_value"
	| "party_floor_2_k_value"
	| "party_floor_3_k_value"
	| "party_floor_4_k_value"
	| "party_wall_1_k_value"
	| "party_wall_1_u_value"
	| "party_wall_2_k_value"
	| "party_wall_2_u_value"
	| "party_wall_3_k_value"
	| "party_wall_3_u_value"
	| "party_wall_4_k_value"
	| "party_wall_4_u_value"
	| "roof_1_k_value"
	| "roof_1_u_value"
	| "roof_2_k_value"
	| "roof_2_u_value"
	| "roof_3_k_value"
	| "roof_3_u_value"
	| "roof_4_k_value"
	| "roof_4_u_value"
	| "semi_glazed_door_u_value"
	| "solid_door_u_value"
	| "tenure"
	| "epc_rating_grade";
export type MetricsAccessors =
	| "metrics.ap4"
	| "metrics.ap50"
	| "metrics.balanced_heat_recovery_efficiency"
	| "metrics.basement_floor_1_k_value"
	| "metrics.basement_floor_1_u_value"
	| "metrics.basement_floor_2_k_value"
	| "metrics.basement_floor_2_u_value"
	| "metrics.basement_floor_3_k_value"
	| "metrics.basement_floor_3_u_value"
	| "metrics.basement_floor_4_k_value"
	| "metrics.basement_floor_4_u_value"
	| "metrics.basement_wall_1_k_value"
	| "metrics.basement_wall_1_u_value"
	| "metrics.basement_wall_2_k_value"
	| "metrics.basement_wall_2_u_value"
	| "metrics.basement_wall_3_k_value"
	| "metrics.basement_wall_3_u_value"
	| "metrics.basement_wall_4_k_value"
	| "metrics.basement_wall_4_u_value"
	| "metrics.custom_measures"
	| "metrics.dynamic_measures"
	| "metrics.epc_rating"
	| "metrics.exposed_floor_1_k_value"
	| "metrics.exposed_floor_1_u_value"
	| "metrics.exposed_floor_2_k_value"
	| "metrics.exposed_floor_2_u_value"
	| "metrics.exposed_floor_3_k_value"
	| "metrics.exposed_floor_3_u_value"
	| "metrics.exposed_floor_4_k_value"
	| "metrics.exposed_floor_4_u_value"
	| "metrics.external_wall_1_k_value"
	| "metrics.external_wall_1_u_value"
	| "metrics.external_wall_2_k_value"
	| "metrics.external_wall_2_u_value"
	| "metrics.external_wall_3_k_value"
	| "metrics.external_wall_3_u_value"
	| "metrics.external_wall_4_k_value"
	| "metrics.external_wall_4_u_value"
	| "metrics.ground_floor_1_k_value"
	| "metrics.ground_floor_1_u_value"
	| "metrics.ground_floor_2_k_value"
	| "metrics.ground_floor_2_u_value"
	| "metrics.ground_floor_3_k_value"
	| "metrics.ground_floor_3_u_value"
	| "metrics.ground_floor_4_k_value"
	| "metrics.ground_floor_4_u_value"
	| "metrics.internal_ceiling_1_k_value"
	| "metrics.internal_ceiling_2_k_value"
	| "metrics.internal_ceiling_3_k_value"
	| "metrics.internal_ceiling_4_k_value"
	| "metrics.internal_floor_1_k_value"
	| "metrics.internal_floor_2_k_value"
	| "metrics.internal_floor_3_k_value"
	| "metrics.internal_floor_4_k_value"
	| "metrics.internal_wall_1_k_value"
	| "metrics.internal_wall_2_k_value"
	| "metrics.internal_wall_3_k_value"
	| "metrics.internal_wall_4_k_value"
	| "metrics.lodged_epc_rating_grade"
	| "metrics.lodged_sap_score"
	| "metrics.odc_last_accepted_suggestion"
	| "metrics.odc_lodgement_date"
	| "metrics.party_ceiling_1_k_value"
	| "metrics.party_ceiling_2_k_value"
	| "metrics.party_ceiling_3_k_value"
	| "metrics.party_ceiling_4_k_value"
	| "metrics.party_floor_1_k_value"
	| "metrics.party_floor_2_k_value"
	| "metrics.party_floor_3_k_value"
	| "metrics.party_floor_4_k_value"
	| "metrics.party_wall_1_k_value"
	| "metrics.party_wall_1_u_value"
	| "metrics.party_wall_2_k_value"
	| "metrics.party_wall_2_u_value"
	| "metrics.party_wall_3_k_value"
	| "metrics.party_wall_3_u_value"
	| "metrics.party_wall_4_k_value"
	| "metrics.party_wall_4_u_value"
	| "metrics.roof_1_k_value"
	| "metrics.roof_1_u_value"
	| "metrics.roof_2_k_value"
	| "metrics.roof_2_u_value"
	| "metrics.roof_3_k_value"
	| "metrics.roof_3_u_value"
	| "metrics.roof_4_k_value"
	| "metrics.roof_4_u_value"
	| "metrics.semi_glazed_door_u_value"
	| "metrics.solid_door_u_value"
	| "metrics.tenure"
	| "metrics.epc_rating_grade";
export type MiscAttributes =
	| "anomaly"
	| "block_ref"
	| "cell_colour_id"
	| "classification"
	| "csco"
	| "funding_applied"
	| "grant_amount"
	| "grant_info"
	| "habitable_rooms"
	| "housing_association"
	| "no_lens"
	| "notes_1"
	| "notes_2"
	| "property_description"
	| "responsiveness"
	| "scheme_ref";
export type MiscAccessors =
	| "misc.anomaly"
	| "misc.block_ref"
	| "misc.cell_colour_id"
	| "misc.classification"
	| "misc.csco"
	| "misc.funding_applied"
	| "misc.grant_amount"
	| "misc.grant_info"
	| "misc.habitable_rooms"
	| "misc.housing_association"
	| "misc.no_lens"
	| "misc.notes_1"
	| "misc.notes_2"
	| "misc.property_description"
	| "misc.responsiveness"
	| "misc.scheme_ref";
export type RoofAttributes =
	| "chimney"
	| "roof_area"
	| "roof_construction"
	| "roof_construction_type"
	| "roof_description"
	| "roof_type"
	| "rooforientation"
	| "roofshading"
	| "rooftilt";
export type RoofAccessors =
	| "roof.chimney"
	| "roof.roof_area"
	| "roof.roof_construction"
	| "roof.roof_construction_type"
	| "roof.roof_description"
	| "roof.roof_type"
	| "roof.rooforientation"
	| "roof.roofshading"
	| "roof.rooftilt";
export type SapDataAttributes =
	| "address_city"
	| "address_house_info_0"
	| "address_house_info_1"
	| "address_postcode"
	| "address_street_name"
	| "area_of_concern"
	| "as_built_co2_rating"
	| "as_built_co2_use"
	| "as_built_energy_rating"
	| "as_built_energy_use"
	| "as_built_heat_demand"
	| "as_built_heating_cost"
	| "as_built_hlp"
	| "as_built_hot_water_cost"
	| "as_built_lighting_cost"
	| "avg_window"
	| "bis_roof_image"
	| "country_code"
	| "current_co2_rating"
	| "current_co2_use"
	| "current_energy_rating"
	| "current_energy_use"
	| "current_heat_demand"
	| "current_heating_cost"
	| "current_hlp"
	| "current_hot_water_cost"
	| "current_lighting_cost"
	| "envision_factor"
	| "envision_factor_prev"
	| "improved_air_source_heat_energy_saving"
	| "improved_air_source_heat_heat_demand"
	| "improved_air_source_heat_hlp"
	| "improved_cbr_co2_rating"
	| "improved_ch_co2_rating"
	| "improved_ch_co2_saving"
	| "improved_ch_co2_value"
	| "improved_ch_cost_saving"
	| "improved_ch_energy_saving"
	| "improved_ch_heat_demand"
	| "improved_ch_hlp"
	| "improved_ch_sap_rating"
	| "improved_ch_sap_value"
	| "improved_dg_a_co2_rating"
	| "improved_dg_a_co2_saving"
	| "improved_dg_a_co2_value"
	| "improved_dg_a_cost_saving"
	| "improved_dg_a_energy_saving"
	| "improved_dg_a_heat_demand"
	| "improved_dg_a_hlp"
	| "improved_dg_asap_rating"
	| "improved_dg_asap_value"
	| "improved_dg_b_co2_rating"
	| "improved_dg_b_co2_saving"
	| "improved_dg_b_co2_value"
	| "improved_dg_b_cost_saving"
	| "improved_dg_b_energy_saving"
	| "improved_dg_b_sap_rating"
	| "improved_dg_b_sap_value"
	| "improved_dg_triple_energy_saving"
	| "improved_dg_triple_heat_demand"
	| "improved_dg_triple_hlp"
	| "improved_dp_co2_rating"
	| "improved_dp_co2_saving"
	| "improved_dp_co2_value"
	| "improved_dp_cost_saving"
	| "improved_dp_energy_saving"
	| "improved_dp_heat_demand"
	| "improved_dp_hlp"
	| "improved_dpsap_rating"
	| "improved_dpsap_value"
	| "improved_ewi100_energy_saving"
	| "improved_ewi100_heat_demand"
	| "improved_ewi100_hlp"
	| "improved_ewi150_energy_saving"
	| "improved_ewi150_heat_demand"
	| "improved_ewi150_hlp"
	| "improved_ewi200_energy_saving"
	| "improved_ewi200_heat_demand"
	| "improved_ewi200_hlp"
	| "improved_ewi50_energy_saving"
	| "improved_ewi50_heat_demand"
	| "improved_ewi50_hlp"
	| "improved_ground_source_heat_energy_saving"
	| "improved_ground_source_heat_heat_demand"
	| "improved_ground_source_heat_hlp"
	| "improved_heating_controls_energy_saving"
	| "improved_heating_controls_heat_demand"
	| "improved_heating_controls_hlp"
	| "improved_lighting_co2_rating"
	| "improved_lighting_co2_saving"
	| "improved_lighting_co2_value"
	| "improved_lighting_cost_saving"
	| "improved_lighting_energy_saving"
	| "improved_lighting_heat_demand"
	| "improved_lighting_hlp"
	| "improved_lighting_roof_energy_saving"
	| "improved_lighting_roof_heat_demand"
	| "improved_lighting_roof_hlp"
	| "improved_lighting_roof_wall_controls_ashp_energy_saving"
	| "improved_lighting_roof_wall_controls_ashp_heat_demand"
	| "improved_lighting_roof_wall_controls_ashp_hlp"
	| "improved_lighting_roof_wall_controls_energy_saving"
	| "improved_lighting_roof_wall_controls_heat_demand"
	| "improved_lighting_roof_wall_controls_heating_energy_saving"
	| "improved_lighting_roof_wall_controls_heating_heat_demand"
	| "improved_lighting_roof_wall_controls_heating_hlp"
	| "improved_lighting_roof_wall_controls_heating_pv25_energy_saving"
	| "improved_lighting_roof_wall_controls_heating_pv25_heat_demand"
	| "improved_lighting_roof_wall_controls_heating_pv25_hlp"
	| "improved_lighting_roof_wall_controls_hlp"
	| "improved_lighting_roof_wall_controls_pv25_ashp_energy_saving"
	| "improved_lighting_roof_wall_controls_pv25_ashp_heat_demand"
	| "improved_lighting_roof_wall_controls_pv25_ashp_hlp"
	| "improved_lighting_roof_wall_controls_pv25_energy_saving"
	| "improved_lighting_roof_wall_controls_pv25_heat_demand"
	| "improved_lighting_roof_wall_controls_pv25_hlp"
	| "improved_lighting_roof_wall_energy_saving"
	| "improved_lighting_roof_wall_heat_demand"
	| "improved_lighting_roof_wall_hlp"
	| "improved_lighting_sap_rating"
	| "improved_lighting_sap_value"
	| "improved_loft400_energy_saving"
	| "improved_loft400_heat_demand"
	| "improved_loft400_hlp"
	| "improved_main_heat2_gas_energy_saving"
	| "improved_main_heat2_gas_heat_demand"
	| "improved_main_heat2_gas_hlp"
	| "improved_party_wall_energy_saving"
	| "improved_party_wall_heat_demand"
	| "improved_party_wall_hlp"
	| "improved_pv15kw_energy_saving"
	| "improved_pv15kw_heat_demand"
	| "improved_pv15kw_hlp"
	| "improved_pv25kw_energy_saving"
	| "improved_pv25kw_heat_demand"
	| "improved_pv25kw_hlp"
	| "improved_pv35kw_energy_saving"
	| "improved_pv35kw_heat_demand"
	| "improved_pv35kw_hlp"
	| "improved_roof_co2_rating"
	| "improved_roof_co2_saving"
	| "improved_roof_co2_value"
	| "improved_roof_cost_saving"
	| "improved_roof_energy_saving"
	| "improved_roof_heat_demand"
	| "improved_roof_hlp"
	| "improved_roof_sap_rating"
	| "improved_roof_sap_value"
	| "improved_smart_controls_energy_saving"
	| "improved_smart_controls_heat_demand"
	| "improved_smart_controls_hlp"
	| "improved_solar_thermal_energy_saving"
	| "improved_solar_thermal_heat_demand"
	| "improved_solar_thermal_hlp"
	| "improved_to_cbr_co2_saving"
	| "improved_to_cbr_co2_value"
	| "improved_to_cbr_cost_saving"
	| "improved_to_cbr_energy_saving"
	| "improved_to_cbr_heat_demand"
	| "improved_to_cbr_hlp"
	| "improved_to_cbr_sap_rating"
	| "improved_to_cbr_sap_value"
	| "improved_underfloor_insulation_energy_saving"
	| "improved_underfloor_insulation_heat_demand"
	| "improved_underfloor_insulation_hlp"
	| "improved_walls_co2_rating"
	| "improved_walls_co2_saving"
	| "improved_walls_co2_value"
	| "improved_walls_cost_saving"
	| "improved_walls_energy_saving"
	| "improved_walls_heat_demand"
	| "improved_walls_hlp"
	| "improved_walls_sap_rating"
	| "improved_walls_sap_value"
	| "improvedairsourceheatco2saving"
	| "improvedairsourceheatco2value"
	| "improvedairsourceheatcostsaving"
	| "improvedairsourceheatsapvalue"
	| "improveddg_tripleco2saving"
	| "improveddg_tripleco2value"
	| "improveddg_triplecostsaving"
	| "improveddg_triplesapvalue"
	| "improvedewi100co2saving"
	| "improvedewi100co2value"
	| "improvedewi100costsaving"
	| "improvedewi100sapvalue"
	| "improvedewi150co2saving"
	| "improvedewi150co2value"
	| "improvedewi150costsaving"
	| "improvedewi150sapvalue"
	| "improvedewi200co2saving"
	| "improvedewi200co2value"
	| "improvedewi200costsaving"
	| "improvedewi200sapvalue"
	| "improvedewi50co2saving"
	| "improvedewi50co2value"
	| "improvedewi50costsaving"
	| "improvedewi50sapvalue"
	| "improvedgroundsourceheatco2saving"
	| "improvedgroundsourceheatco2value"
	| "improvedgroundsourceheatcostsaving"
	| "improvedgroundsourceheatsapvalue"
	| "improvedheatingcontrolsco2saving"
	| "improvedheatingcontrolsco2value"
	| "improvedheatingcontrolscostsaving"
	| "improvedheatingcontrolssapvalue"
	| "improvedlighting_roof_wall_controls_ashpco2saving"
	| "improvedlighting_roof_wall_controls_ashpco2value"
	| "improvedlighting_roof_wall_controls_ashpcostsaving"
	| "improvedlighting_roof_wall_controls_ashpsapvalue"
	| "improvedlighting_roof_wall_controls_heating_pv25co2saving"
	| "improvedlighting_roof_wall_controls_heating_pv25co2value"
	| "improvedlighting_roof_wall_controls_heating_pv25costsaving"
	| "improvedlighting_roof_wall_controls_heating_pv25sapvalue"
	| "improvedlighting_roof_wall_controls_heatingco2saving"
	| "improvedlighting_roof_wall_controls_heatingco2value"
	| "improvedlighting_roof_wall_controls_heatingcostsaving"
	| "improvedlighting_roof_wall_controls_heatingsapvalue"
	| "improvedlighting_roof_wall_controls_pv25_ashpco2saving"
	| "improvedlighting_roof_wall_controls_pv25_ashpco2value"
	| "improvedlighting_roof_wall_controls_pv25_ashpcostsaving"
	| "improvedlighting_roof_wall_controls_pv25_ashpsapvalue"
	| "improvedlighting_roof_wall_controls_pv25co2saving"
	| "improvedlighting_roof_wall_controls_pv25co2value"
	| "improvedlighting_roof_wall_controls_pv25costsaving"
	| "improvedlighting_roof_wall_controls_pv25sapvalue"
	| "improvedlighting_roof_wall_controlsco2saving"
	| "improvedlighting_roof_wall_controlsco2value"
	| "improvedlighting_roof_wall_controlscostsaving"
	| "improvedlighting_roof_wall_controlssapvalue"
	| "improvedlighting_roof_wallco2saving"
	| "improvedlighting_roof_wallco2value"
	| "improvedlighting_roof_wallcostsaving"
	| "improvedlighting_roof_wallsapvalue"
	| "improvedlighting_roofco2saving"
	| "improvedlighting_roofco2value"
	| "improvedlighting_roofcostsaving"
	| "improvedlighting_roofsapvalue"
	| "improvedloft400co2saving"
	| "improvedloft400co2value"
	| "improvedloft400costsaving"
	| "improvedloft400sapvalue"
	| "improvedmainheat2gasco2saving"
	| "improvedmainheat2gasco2value"
	| "improvedmainheat2gascostsaving"
	| "improvedmainheat2gassapvalue"
	| "improvedpartywallco2saving"
	| "improvedpartywallco2value"
	| "improvedpartywallcostsaving"
	| "improvedpartywallsapvalue"
	| "improvedpv15kwco2saving"
	| "improvedpv15kwco2value"
	| "improvedpv15kwcostsaving"
	| "improvedpv15kwsapvalue"
	| "improvedpv25kwco2saving"
	| "improvedpv25kwco2value"
	| "improvedpv25kwcostsaving"
	| "improvedpv25kwsapvalue"
	| "improvedpv35kwco2saving"
	| "improvedpv35kwco2value"
	| "improvedpv35kwcostsaving"
	| "improvedpv35kwsapvalue"
	| "improvedsmartcontrolsco2saving"
	| "improvedsmartcontrolsco2value"
	| "improvedsmartcontrolscostsaving"
	| "improvedsmartcontrolssapvalue"
	| "improvedsolarthermalco2saving"
	| "improvedsolarthermalco2value"
	| "improvedsolarthermalcostsaving"
	| "improvedsolarthermalsapvalue"
	| "improvedunderfloorinsulationco2saving"
	| "improvedunderfloorinsulationco2value"
	| "improvedunderfloorinsulationcostsaving"
	| "improvedunderfloorinsulationsapvalue"
	| "ir_image_ref"
	| "is_keep"
	| "is_other_max"
	| "is_other_min"
	| "left_shift"
	| "lens_angle"
	| "mid_point_percentage"
	| "object_distance"
	| "object_distance_factor"
	| "potential_co2"
	| "potential_co2_rating"
	| "potential_energy_rating"
	| "potential_energy_use"
	| "potential_heat_demand"
	| "potential_heating_cost"
	| "potential_hlp"
	| "potential_hot_water_cost"
	| "potential_lighting_cost"
	| "primary_energy_cost"
	| "property_reference"
	| "reference_text"
	| "right_shift"
	| "secondary_energy_cost"
	| "significant_pcnt"
	| "std_deviation"
	| "too_cold_pcnt"
	| "too_hot_pcnt"
	| "total_cost_current"
	| "total_cost_potential"
	| "uk_co2"
	| "uk_cost"
	| "uk_energy"
	| "uk_fuel_cost_multiplier"
	| "updated_at"
	| "potential_epc_rating_grade"
	| "potential_epc_rating_improvement";
export type SapDataAccessors =
	| "sap_data.address_city"
	| "sap_data.address_house_info_0"
	| "sap_data.address_house_info_1"
	| "sap_data.address_postcode"
	| "sap_data.address_street_name"
	| "sap_data.area_of_concern"
	| "sap_data.as_built_co2_rating"
	| "sap_data.as_built_co2_use"
	| "sap_data.as_built_energy_rating"
	| "sap_data.as_built_energy_use"
	| "sap_data.as_built_heat_demand"
	| "sap_data.as_built_heating_cost"
	| "sap_data.as_built_hlp"
	| "sap_data.as_built_hot_water_cost"
	| "sap_data.as_built_lighting_cost"
	| "sap_data.avg_window"
	| "sap_data.bis_roof_image"
	| "sap_data.country_code"
	| "sap_data.current_co2_rating"
	| "sap_data.current_co2_use"
	| "sap_data.current_energy_rating"
	| "sap_data.current_energy_use"
	| "sap_data.current_heat_demand"
	| "sap_data.current_heating_cost"
	| "sap_data.current_hlp"
	| "sap_data.current_hot_water_cost"
	| "sap_data.current_lighting_cost"
	| "sap_data.envision_factor"
	| "sap_data.envision_factor_prev"
	| "sap_data.improved_air_source_heat_energy_saving"
	| "sap_data.improved_air_source_heat_heat_demand"
	| "sap_data.improved_air_source_heat_hlp"
	| "sap_data.improved_cbr_co2_rating"
	| "sap_data.improved_ch_co2_rating"
	| "sap_data.improved_ch_co2_saving"
	| "sap_data.improved_ch_co2_value"
	| "sap_data.improved_ch_cost_saving"
	| "sap_data.improved_ch_energy_saving"
	| "sap_data.improved_ch_heat_demand"
	| "sap_data.improved_ch_hlp"
	| "sap_data.improved_ch_sap_rating"
	| "sap_data.improved_ch_sap_value"
	| "sap_data.improved_dg_a_co2_rating"
	| "sap_data.improved_dg_a_co2_saving"
	| "sap_data.improved_dg_a_co2_value"
	| "sap_data.improved_dg_a_cost_saving"
	| "sap_data.improved_dg_a_energy_saving"
	| "sap_data.improved_dg_a_heat_demand"
	| "sap_data.improved_dg_a_hlp"
	| "sap_data.improved_dg_asap_rating"
	| "sap_data.improved_dg_asap_value"
	| "sap_data.improved_dg_b_co2_rating"
	| "sap_data.improved_dg_b_co2_saving"
	| "sap_data.improved_dg_b_co2_value"
	| "sap_data.improved_dg_b_cost_saving"
	| "sap_data.improved_dg_b_energy_saving"
	| "sap_data.improved_dg_b_sap_rating"
	| "sap_data.improved_dg_b_sap_value"
	| "sap_data.improved_dg_triple_energy_saving"
	| "sap_data.improved_dg_triple_heat_demand"
	| "sap_data.improved_dg_triple_hlp"
	| "sap_data.improved_dp_co2_rating"
	| "sap_data.improved_dp_co2_saving"
	| "sap_data.improved_dp_co2_value"
	| "sap_data.improved_dp_cost_saving"
	| "sap_data.improved_dp_energy_saving"
	| "sap_data.improved_dp_heat_demand"
	| "sap_data.improved_dp_hlp"
	| "sap_data.improved_dpsap_rating"
	| "sap_data.improved_dpsap_value"
	| "sap_data.improved_ewi100_energy_saving"
	| "sap_data.improved_ewi100_heat_demand"
	| "sap_data.improved_ewi100_hlp"
	| "sap_data.improved_ewi150_energy_saving"
	| "sap_data.improved_ewi150_heat_demand"
	| "sap_data.improved_ewi150_hlp"
	| "sap_data.improved_ewi200_energy_saving"
	| "sap_data.improved_ewi200_heat_demand"
	| "sap_data.improved_ewi200_hlp"
	| "sap_data.improved_ewi50_energy_saving"
	| "sap_data.improved_ewi50_heat_demand"
	| "sap_data.improved_ewi50_hlp"
	| "sap_data.improved_ground_source_heat_energy_saving"
	| "sap_data.improved_ground_source_heat_heat_demand"
	| "sap_data.improved_ground_source_heat_hlp"
	| "sap_data.improved_heating_controls_energy_saving"
	| "sap_data.improved_heating_controls_heat_demand"
	| "sap_data.improved_heating_controls_hlp"
	| "sap_data.improved_lighting_co2_rating"
	| "sap_data.improved_lighting_co2_saving"
	| "sap_data.improved_lighting_co2_value"
	| "sap_data.improved_lighting_cost_saving"
	| "sap_data.improved_lighting_energy_saving"
	| "sap_data.improved_lighting_heat_demand"
	| "sap_data.improved_lighting_hlp"
	| "sap_data.improved_lighting_roof_energy_saving"
	| "sap_data.improved_lighting_roof_heat_demand"
	| "sap_data.improved_lighting_roof_hlp"
	| "sap_data.improved_lighting_roof_wall_controls_ashp_energy_saving"
	| "sap_data.improved_lighting_roof_wall_controls_ashp_heat_demand"
	| "sap_data.improved_lighting_roof_wall_controls_ashp_hlp"
	| "sap_data.improved_lighting_roof_wall_controls_energy_saving"
	| "sap_data.improved_lighting_roof_wall_controls_heat_demand"
	| "sap_data.improved_lighting_roof_wall_controls_heating_energy_saving"
	| "sap_data.improved_lighting_roof_wall_controls_heating_heat_demand"
	| "sap_data.improved_lighting_roof_wall_controls_heating_hlp"
	| "sap_data.improved_lighting_roof_wall_controls_heating_pv25_energy_saving"
	| "sap_data.improved_lighting_roof_wall_controls_heating_pv25_heat_demand"
	| "sap_data.improved_lighting_roof_wall_controls_heating_pv25_hlp"
	| "sap_data.improved_lighting_roof_wall_controls_hlp"
	| "sap_data.improved_lighting_roof_wall_controls_pv25_ashp_energy_saving"
	| "sap_data.improved_lighting_roof_wall_controls_pv25_ashp_heat_demand"
	| "sap_data.improved_lighting_roof_wall_controls_pv25_ashp_hlp"
	| "sap_data.improved_lighting_roof_wall_controls_pv25_energy_saving"
	| "sap_data.improved_lighting_roof_wall_controls_pv25_heat_demand"
	| "sap_data.improved_lighting_roof_wall_controls_pv25_hlp"
	| "sap_data.improved_lighting_roof_wall_energy_saving"
	| "sap_data.improved_lighting_roof_wall_heat_demand"
	| "sap_data.improved_lighting_roof_wall_hlp"
	| "sap_data.improved_lighting_sap_rating"
	| "sap_data.improved_lighting_sap_value"
	| "sap_data.improved_loft400_energy_saving"
	| "sap_data.improved_loft400_heat_demand"
	| "sap_data.improved_loft400_hlp"
	| "sap_data.improved_main_heat2_gas_energy_saving"
	| "sap_data.improved_main_heat2_gas_heat_demand"
	| "sap_data.improved_main_heat2_gas_hlp"
	| "sap_data.improved_party_wall_energy_saving"
	| "sap_data.improved_party_wall_heat_demand"
	| "sap_data.improved_party_wall_hlp"
	| "sap_data.improved_pv15kw_energy_saving"
	| "sap_data.improved_pv15kw_heat_demand"
	| "sap_data.improved_pv15kw_hlp"
	| "sap_data.improved_pv25kw_energy_saving"
	| "sap_data.improved_pv25kw_heat_demand"
	| "sap_data.improved_pv25kw_hlp"
	| "sap_data.improved_pv35kw_energy_saving"
	| "sap_data.improved_pv35kw_heat_demand"
	| "sap_data.improved_pv35kw_hlp"
	| "sap_data.improved_roof_co2_rating"
	| "sap_data.improved_roof_co2_saving"
	| "sap_data.improved_roof_co2_value"
	| "sap_data.improved_roof_cost_saving"
	| "sap_data.improved_roof_energy_saving"
	| "sap_data.improved_roof_heat_demand"
	| "sap_data.improved_roof_hlp"
	| "sap_data.improved_roof_sap_rating"
	| "sap_data.improved_roof_sap_value"
	| "sap_data.improved_smart_controls_energy_saving"
	| "sap_data.improved_smart_controls_heat_demand"
	| "sap_data.improved_smart_controls_hlp"
	| "sap_data.improved_solar_thermal_energy_saving"
	| "sap_data.improved_solar_thermal_heat_demand"
	| "sap_data.improved_solar_thermal_hlp"
	| "sap_data.improved_to_cbr_co2_saving"
	| "sap_data.improved_to_cbr_co2_value"
	| "sap_data.improved_to_cbr_cost_saving"
	| "sap_data.improved_to_cbr_energy_saving"
	| "sap_data.improved_to_cbr_heat_demand"
	| "sap_data.improved_to_cbr_hlp"
	| "sap_data.improved_to_cbr_sap_rating"
	| "sap_data.improved_to_cbr_sap_value"
	| "sap_data.improved_underfloor_insulation_energy_saving"
	| "sap_data.improved_underfloor_insulation_heat_demand"
	| "sap_data.improved_underfloor_insulation_hlp"
	| "sap_data.improved_walls_co2_rating"
	| "sap_data.improved_walls_co2_saving"
	| "sap_data.improved_walls_co2_value"
	| "sap_data.improved_walls_cost_saving"
	| "sap_data.improved_walls_energy_saving"
	| "sap_data.improved_walls_heat_demand"
	| "sap_data.improved_walls_hlp"
	| "sap_data.improved_walls_sap_rating"
	| "sap_data.improved_walls_sap_value"
	| "sap_data.improvedairsourceheatco2saving"
	| "sap_data.improvedairsourceheatco2value"
	| "sap_data.improvedairsourceheatcostsaving"
	| "sap_data.improvedairsourceheatsapvalue"
	| "sap_data.improveddg_tripleco2saving"
	| "sap_data.improveddg_tripleco2value"
	| "sap_data.improveddg_triplecostsaving"
	| "sap_data.improveddg_triplesapvalue"
	| "sap_data.improvedewi100co2saving"
	| "sap_data.improvedewi100co2value"
	| "sap_data.improvedewi100costsaving"
	| "sap_data.improvedewi100sapvalue"
	| "sap_data.improvedewi150co2saving"
	| "sap_data.improvedewi150co2value"
	| "sap_data.improvedewi150costsaving"
	| "sap_data.improvedewi150sapvalue"
	| "sap_data.improvedewi200co2saving"
	| "sap_data.improvedewi200co2value"
	| "sap_data.improvedewi200costsaving"
	| "sap_data.improvedewi200sapvalue"
	| "sap_data.improvedewi50co2saving"
	| "sap_data.improvedewi50co2value"
	| "sap_data.improvedewi50costsaving"
	| "sap_data.improvedewi50sapvalue"
	| "sap_data.improvedgroundsourceheatco2saving"
	| "sap_data.improvedgroundsourceheatco2value"
	| "sap_data.improvedgroundsourceheatcostsaving"
	| "sap_data.improvedgroundsourceheatsapvalue"
	| "sap_data.improvedheatingcontrolsco2saving"
	| "sap_data.improvedheatingcontrolsco2value"
	| "sap_data.improvedheatingcontrolscostsaving"
	| "sap_data.improvedheatingcontrolssapvalue"
	| "sap_data.improvedlighting_roof_wall_controls_ashpco2saving"
	| "sap_data.improvedlighting_roof_wall_controls_ashpco2value"
	| "sap_data.improvedlighting_roof_wall_controls_ashpcostsaving"
	| "sap_data.improvedlighting_roof_wall_controls_ashpsapvalue"
	| "sap_data.improvedlighting_roof_wall_controls_heating_pv25co2saving"
	| "sap_data.improvedlighting_roof_wall_controls_heating_pv25co2value"
	| "sap_data.improvedlighting_roof_wall_controls_heating_pv25costsaving"
	| "sap_data.improvedlighting_roof_wall_controls_heating_pv25sapvalue"
	| "sap_data.improvedlighting_roof_wall_controls_heatingco2saving"
	| "sap_data.improvedlighting_roof_wall_controls_heatingco2value"
	| "sap_data.improvedlighting_roof_wall_controls_heatingcostsaving"
	| "sap_data.improvedlighting_roof_wall_controls_heatingsapvalue"
	| "sap_data.improvedlighting_roof_wall_controls_pv25_ashpco2saving"
	| "sap_data.improvedlighting_roof_wall_controls_pv25_ashpco2value"
	| "sap_data.improvedlighting_roof_wall_controls_pv25_ashpcostsaving"
	| "sap_data.improvedlighting_roof_wall_controls_pv25_ashpsapvalue"
	| "sap_data.improvedlighting_roof_wall_controls_pv25co2saving"
	| "sap_data.improvedlighting_roof_wall_controls_pv25co2value"
	| "sap_data.improvedlighting_roof_wall_controls_pv25costsaving"
	| "sap_data.improvedlighting_roof_wall_controls_pv25sapvalue"
	| "sap_data.improvedlighting_roof_wall_controlsco2saving"
	| "sap_data.improvedlighting_roof_wall_controlsco2value"
	| "sap_data.improvedlighting_roof_wall_controlscostsaving"
	| "sap_data.improvedlighting_roof_wall_controlssapvalue"
	| "sap_data.improvedlighting_roof_wallco2saving"
	| "sap_data.improvedlighting_roof_wallco2value"
	| "sap_data.improvedlighting_roof_wallcostsaving"
	| "sap_data.improvedlighting_roof_wallsapvalue"
	| "sap_data.improvedlighting_roofco2saving"
	| "sap_data.improvedlighting_roofco2value"
	| "sap_data.improvedlighting_roofcostsaving"
	| "sap_data.improvedlighting_roofsapvalue"
	| "sap_data.improvedloft400co2saving"
	| "sap_data.improvedloft400co2value"
	| "sap_data.improvedloft400costsaving"
	| "sap_data.improvedloft400sapvalue"
	| "sap_data.improvedmainheat2gasco2saving"
	| "sap_data.improvedmainheat2gasco2value"
	| "sap_data.improvedmainheat2gascostsaving"
	| "sap_data.improvedmainheat2gassapvalue"
	| "sap_data.improvedpartywallco2saving"
	| "sap_data.improvedpartywallco2value"
	| "sap_data.improvedpartywallcostsaving"
	| "sap_data.improvedpartywallsapvalue"
	| "sap_data.improvedpv15kwco2saving"
	| "sap_data.improvedpv15kwco2value"
	| "sap_data.improvedpv15kwcostsaving"
	| "sap_data.improvedpv15kwsapvalue"
	| "sap_data.improvedpv25kwco2saving"
	| "sap_data.improvedpv25kwco2value"
	| "sap_data.improvedpv25kwcostsaving"
	| "sap_data.improvedpv25kwsapvalue"
	| "sap_data.improvedpv35kwco2saving"
	| "sap_data.improvedpv35kwco2value"
	| "sap_data.improvedpv35kwcostsaving"
	| "sap_data.improvedpv35kwsapvalue"
	| "sap_data.improvedsmartcontrolsco2saving"
	| "sap_data.improvedsmartcontrolsco2value"
	| "sap_data.improvedsmartcontrolscostsaving"
	| "sap_data.improvedsmartcontrolssapvalue"
	| "sap_data.improvedsolarthermalco2saving"
	| "sap_data.improvedsolarthermalco2value"
	| "sap_data.improvedsolarthermalcostsaving"
	| "sap_data.improvedsolarthermalsapvalue"
	| "sap_data.improvedunderfloorinsulationco2saving"
	| "sap_data.improvedunderfloorinsulationco2value"
	| "sap_data.improvedunderfloorinsulationcostsaving"
	| "sap_data.improvedunderfloorinsulationsapvalue"
	| "sap_data.ir_image_ref"
	| "sap_data.is_keep"
	| "sap_data.is_other_max"
	| "sap_data.is_other_min"
	| "sap_data.left_shift"
	| "sap_data.lens_angle"
	| "sap_data.mid_point_percentage"
	| "sap_data.object_distance"
	| "sap_data.object_distance_factor"
	| "sap_data.potential_co2"
	| "sap_data.potential_co2_rating"
	| "sap_data.potential_energy_rating"
	| "sap_data.potential_energy_use"
	| "sap_data.potential_heat_demand"
	| "sap_data.potential_heating_cost"
	| "sap_data.potential_hlp"
	| "sap_data.potential_hot_water_cost"
	| "sap_data.potential_lighting_cost"
	| "sap_data.primary_energy_cost"
	| "sap_data.property_reference"
	| "sap_data.reference_text"
	| "sap_data.right_shift"
	| "sap_data.secondary_energy_cost"
	| "sap_data.significant_pcnt"
	| "sap_data.std_deviation"
	| "sap_data.too_cold_pcnt"
	| "sap_data.too_hot_pcnt"
	| "sap_data.total_cost_current"
	| "sap_data.total_cost_potential"
	| "sap_data.uk_co2"
	| "sap_data.uk_cost"
	| "sap_data.uk_energy"
	| "sap_data.uk_fuel_cost_multiplier"
	| "sap_data.updated_at"
	| "sap_data.potential_epc_rating_grade"
	| "sap_data.potential_epc_rating_improvement";
export type SecondaryHeatingAttributes =
	| "second_heating_fuel"
	| "second_heating_system"
	| "secondary_co2"
	| "secondary_efficiency"
	| "secondary_fraction"
	| "secondary_fuel_co2"
	| "secondary_fuel_cost"
	| "secondary_fuel_price"
	| "secondary_heating_high_rate_fraction"
	| "secondary_heating_type"
	| "secondary_high_rate_fuel_cost"
	| "secondary_low_rate_fuel_cost"
	| "secondary_space_heating_fuel";
export type SecondaryHeatingAccessors =
	| "secondary_heating.second_heating_fuel"
	| "secondary_heating.second_heating_system"
	| "secondary_heating.secondary_co2"
	| "secondary_heating.secondary_efficiency"
	| "secondary_heating.secondary_fraction"
	| "secondary_heating.secondary_fuel_co2"
	| "secondary_heating.secondary_fuel_cost"
	| "secondary_heating.secondary_fuel_price"
	| "secondary_heating.secondary_heating_high_rate_fraction"
	| "secondary_heating.secondary_heating_type"
	| "secondary_heating.secondary_high_rate_fuel_cost"
	| "secondary_heating.secondary_low_rate_fuel_cost"
	| "secondary_heating.secondary_space_heating_fuel";
export type SolarAttributes =
	| "electricity_generated_by_pvs_price"
	| "meter_export_to_pv"
	| "pvpower"
	| "roofshading"
	| "rooftilt"
	| "solar_east_2_access_factor"
	| "solar_east_2_frame_factor"
	| "solar_east_2_transmittance"
	| "solar_east_2_window_area"
	| "solar_east_access_factor"
	| "solar_east_frame_factor"
	| "solar_east_transmittance"
	| "solar_east_window_area"
	| "solar_north_2_access_factor"
	| "solar_north_2_frame_factor"
	| "solar_north_2_transmittance"
	| "solar_north_2_window_area"
	| "solar_north_access_factor"
	| "solar_north_east_2_access_factor"
	| "solar_north_east_2_frame_factor"
	| "solar_north_east_2_transmittance"
	| "solar_north_east_2_window_area"
	| "solar_north_east_access_factor"
	| "solar_north_east_frame_factor"
	| "solar_north_east_transmittance"
	| "solar_north_east_window_area"
	| "solar_north_frame_factor"
	| "solar_north_transmittance"
	| "solar_north_west_2_access_factor"
	| "solar_north_west_2_frame_factor"
	| "solar_north_west_2_transmittance"
	| "solar_north_west_2_window_area"
	| "solar_north_west_access_factor"
	| "solar_north_west_frame_factor"
	| "solar_north_west_transmittance"
	| "solar_north_west_window_area"
	| "solar_north_window_area"
	| "solar_roof_rooflight_2_area"
	| "solar_roof_rooflight_2_frame_factor"
	| "solar_roof_rooflight_2_tilt"
	| "solar_roof_rooflight_2_transmittance"
	| "solar_roof_rooflight_area"
	| "solar_roof_rooflight_frame_factor"
	| "solar_roof_rooflight_tilt"
	| "solar_roof_rooflight_transmittance"
	| "solar_roof_window_2_access_factor"
	| "solar_roof_window_2_area"
	| "solar_roof_window_2_tilt"
	| "solar_roof_window_2_transmittance"
	| "solar_roof_window_area"
	| "solar_roof_window_frame_factor"
	| "solar_roof_window_tilt"
	| "solar_roof_window_transmittance"
	| "solar_south_2_access_factor"
	| "solar_south_2_frame_factor"
	| "solar_south_2_transmittance"
	| "solar_south_2_window_area"
	| "solar_south_access_factor"
	| "solar_south_east_2_access_factor"
	| "solar_south_east_2_frame_factor"
	| "solar_south_east_2_transmittance"
	| "solar_south_east_2_window_area"
	| "solar_south_east_access_factor"
	| "solar_south_east_frame_factor"
	| "solar_south_east_transmittance"
	| "solar_south_east_window_area"
	| "solar_south_frame_factor"
	| "solar_south_transmittance"
	| "solar_south_west_2_access_factor"
	| "solar_south_west_2_frame_factor"
	| "solar_south_west_2_transmittance"
	| "solar_south_west_2_window_area"
	| "solar_south_west_access_factor"
	| "solar_south_west_frame_factor"
	| "solar_south_west_transmittance"
	| "solar_south_west_window_area"
	| "solar_south_window_area"
	| "solar_storage_volume"
	| "solar_west_2_access_factor"
	| "solar_west_2_frame_factor"
	| "solar_west_2_transmittance"
	| "solar_west_2_window_area"
	| "solar_west_access_factor"
	| "solar_west_frame_factor"
	| "solar_west_transmittance"
	| "solar_west_window_area"
	| "solarpvarea"
	| "solarpvorientation"
	| "solarpvshading"
	| "solarpvtilt"
	| "wh_battery_capacity_kwh"
	| "wh_has_pv_diverter"
	| "wh_has_solar_water_heating"
	| "wh_solar_heating_usage";
export type SolarAccessors =
	| "solar.electricity_generated_by_pvs_price"
	| "solar.meter_export_to_pv"
	| "solar.pvpower"
	| "solar.roofshading"
	| "solar.rooftilt"
	| "solar.solar_east_2_access_factor"
	| "solar.solar_east_2_frame_factor"
	| "solar.solar_east_2_transmittance"
	| "solar.solar_east_2_window_area"
	| "solar.solar_east_access_factor"
	| "solar.solar_east_frame_factor"
	| "solar.solar_east_transmittance"
	| "solar.solar_east_window_area"
	| "solar.solar_north_2_access_factor"
	| "solar.solar_north_2_frame_factor"
	| "solar.solar_north_2_transmittance"
	| "solar.solar_north_2_window_area"
	| "solar.solar_north_access_factor"
	| "solar.solar_north_east_2_access_factor"
	| "solar.solar_north_east_2_frame_factor"
	| "solar.solar_north_east_2_transmittance"
	| "solar.solar_north_east_2_window_area"
	| "solar.solar_north_east_access_factor"
	| "solar.solar_north_east_frame_factor"
	| "solar.solar_north_east_transmittance"
	| "solar.solar_north_east_window_area"
	| "solar.solar_north_frame_factor"
	| "solar.solar_north_transmittance"
	| "solar.solar_north_west_2_access_factor"
	| "solar.solar_north_west_2_frame_factor"
	| "solar.solar_north_west_2_transmittance"
	| "solar.solar_north_west_2_window_area"
	| "solar.solar_north_west_access_factor"
	| "solar.solar_north_west_frame_factor"
	| "solar.solar_north_west_transmittance"
	| "solar.solar_north_west_window_area"
	| "solar.solar_north_window_area"
	| "solar.solar_roof_rooflight_2_area"
	| "solar.solar_roof_rooflight_2_frame_factor"
	| "solar.solar_roof_rooflight_2_tilt"
	| "solar.solar_roof_rooflight_2_transmittance"
	| "solar.solar_roof_rooflight_area"
	| "solar.solar_roof_rooflight_frame_factor"
	| "solar.solar_roof_rooflight_tilt"
	| "solar.solar_roof_rooflight_transmittance"
	| "solar.solar_roof_window_2_access_factor"
	| "solar.solar_roof_window_2_area"
	| "solar.solar_roof_window_2_tilt"
	| "solar.solar_roof_window_2_transmittance"
	| "solar.solar_roof_window_area"
	| "solar.solar_roof_window_frame_factor"
	| "solar.solar_roof_window_tilt"
	| "solar.solar_roof_window_transmittance"
	| "solar.solar_south_2_access_factor"
	| "solar.solar_south_2_frame_factor"
	| "solar.solar_south_2_transmittance"
	| "solar.solar_south_2_window_area"
	| "solar.solar_south_access_factor"
	| "solar.solar_south_east_2_access_factor"
	| "solar.solar_south_east_2_frame_factor"
	| "solar.solar_south_east_2_transmittance"
	| "solar.solar_south_east_2_window_area"
	| "solar.solar_south_east_access_factor"
	| "solar.solar_south_east_frame_factor"
	| "solar.solar_south_east_transmittance"
	| "solar.solar_south_east_window_area"
	| "solar.solar_south_frame_factor"
	| "solar.solar_south_transmittance"
	| "solar.solar_south_west_2_access_factor"
	| "solar.solar_south_west_2_frame_factor"
	| "solar.solar_south_west_2_transmittance"
	| "solar.solar_south_west_2_window_area"
	| "solar.solar_south_west_access_factor"
	| "solar.solar_south_west_frame_factor"
	| "solar.solar_south_west_transmittance"
	| "solar.solar_south_west_window_area"
	| "solar.solar_south_window_area"
	| "solar.solar_storage_volume"
	| "solar.solar_west_2_access_factor"
	| "solar.solar_west_2_frame_factor"
	| "solar.solar_west_2_transmittance"
	| "solar.solar_west_2_window_area"
	| "solar.solar_west_access_factor"
	| "solar.solar_west_frame_factor"
	| "solar.solar_west_transmittance"
	| "solar.solar_west_window_area"
	| "solar.solarpvarea"
	| "solar.solarpvorientation"
	| "solar.solarpvshading"
	| "solar.solarpvtilt"
	| "solar.wh_battery_capacity_kwh"
	| "solar.wh_has_pv_diverter"
	| "solar.wh_has_solar_water_heating"
	| "solar.wh_solar_heating_usage";
export type WaterAttributes =
	| "42a_hws"
	| "baths_count"
	| "cold_water_source"
	| "collectortype"
	| "cylinder_ins_depth"
	| "cylinder_ins_type"
	| "cylinder_volume"
	| "dhw_storage_loss"
	| "has_hot_water_storage"
	| "high_rate_water_cost"
	| "hot_water_control_code"
	| "hot_water_control_type"
	| "hot_water_controls"
	| "hot_water_source"
	| "hot_water_supply_type"
	| "hot_water_type"
	| "htt"
	| "immersion_type"
	| "low_rate_water_cost"
	| "low_water_usage"
	| "pipework_insulation_fraction"
	| "shower_outlet_1_flow_rate"
	| "shower_outlet_1_rated_power"
	| "shower_outlet_2_flow_rate"
	| "shower_outlet_2_rated_power"
	| "shower_outlet_3_flow_rate"
	| "shower_outlet_3_rated_power"
	| "shower_outlet_4_flow_rate"
	| "shower_outlet_4_rated_power"
	| "shower_outlets_count"
	| "shower_type"
	| "showeradjustmentfactor"
	| "storage_loss_factor"
	| "storagevolume"
	| "water_co2"
	| "water_efficiency"
	| "water_heating_fuel"
	| "water_high_rate_fraction"
	| "water_low_rate_fraction"
	| "water_source"
	| "wwhrs"
	| "wwhrs_name"
	| "wwhrs_percent"
	| "wwhrs_utilisation_factor";
export type WaterAccessors =
	| "water.42a_hws"
	| "water.baths_count"
	| "water.cold_water_source"
	| "water.collectortype"
	| "water.cylinder_ins_depth"
	| "water.cylinder_ins_type"
	| "water.cylinder_volume"
	| "water.dhw_storage_loss"
	| "water.has_hot_water_storage"
	| "water.high_rate_water_cost"
	| "water.hot_water_control_code"
	| "water.hot_water_control_type"
	| "water.hot_water_controls"
	| "water.hot_water_source"
	| "water.hot_water_supply_type"
	| "water.hot_water_type"
	| "water.htt"
	| "water.immersion_type"
	| "water.low_rate_water_cost"
	| "water.low_water_usage"
	| "water.pipework_insulation_fraction"
	| "water.shower_outlet_1_flow_rate"
	| "water.shower_outlet_1_rated_power"
	| "water.shower_outlet_2_flow_rate"
	| "water.shower_outlet_2_rated_power"
	| "water.shower_outlet_3_flow_rate"
	| "water.shower_outlet_3_rated_power"
	| "water.shower_outlet_4_flow_rate"
	| "water.shower_outlet_4_rated_power"
	| "water.shower_outlets_count"
	| "water.shower_type"
	| "water.showeradjustmentfactor"
	| "water.storage_loss_factor"
	| "water.storagevolume"
	| "water.water_co2"
	| "water.water_efficiency"
	| "water.water_heating_fuel"
	| "water.water_high_rate_fraction"
	| "water.water_low_rate_fraction"
	| "water.water_source"
	| "water.wwhrs"
	| "water.wwhrs_name"
	| "water.wwhrs_percent"
	| "water.wwhrs_utilisation_factor";
export type WindowsAttributes =
	| "draught_pcnt"
	| "frame_type"
	| "glazing_amount"
	| "glazing_area"
	| "glazing_rating"
	| "glazing_type"
	| "glazing_u_value"
	| "roof_window_1_net_area"
	| "roof_window_1_type"
	| "roof_window_1_u_value"
	| "roof_window_2_net_area"
	| "roof_window_2_u_value"
	| "roof_window_3_net_area"
	| "roof_window_3_u_value"
	| "roof_window_4_net_area"
	| "roof_window_4_u_value"
	| "window_1_net_area"
	| "window_1_type"
	| "window_1_u_value"
	| "window_2_net_area"
	| "window_2_type"
	| "window_2_u_value"
	| "window_3_net_area"
	| "window_3_u_value"
	| "window_4_net_area"
	| "window_4_u_value"
	| "window_details"
	| "window_gap";
export type WindowsAccessors =
	| "windows.draught_pcnt"
	| "windows.frame_type"
	| "windows.glazing_amount"
	| "windows.glazing_area"
	| "windows.glazing_rating"
	| "windows.glazing_type"
	| "windows.glazing_u_value"
	| "windows.roof_window_1_net_area"
	| "windows.roof_window_1_type"
	| "windows.roof_window_1_u_value"
	| "windows.roof_window_2_net_area"
	| "windows.roof_window_2_u_value"
	| "windows.roof_window_3_net_area"
	| "windows.roof_window_3_u_value"
	| "windows.roof_window_4_net_area"
	| "windows.roof_window_4_u_value"
	| "windows.window_1_net_area"
	| "windows.window_1_type"
	| "windows.window_1_u_value"
	| "windows.window_2_net_area"
	| "windows.window_2_type"
	| "windows.window_2_u_value"
	| "windows.window_3_net_area"
	| "windows.window_3_u_value"
	| "windows.window_4_net_area"
	| "windows.window_4_u_value"
	| "windows.window_details"
	| "windows.window_gap";
export type Accessor =
	| AirflowAccessors
	| BuildingAccessors
	| DataIntegrityAccessors
	| DimensionsAccessors
	| ElectricityAccessors
	| HeatingAccessors
	| ImageMetaAccessors
	| LocationAccessors
	| MetricsAccessors
	| MiscAccessors
	| RoofAccessors
	| SapDataAccessors
	| SecondaryHeatingAccessors
	| SolarAccessors
	| WaterAccessors
	| WindowsAccessors;

export type PropertyAttribute = keyof BaseProperty;

export {}; // NB: Workaround for "cannot be compiled"
